import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  LayoutDashboard, UtensilsCrossed, ChefHat, Package,
  Sparkles, Volume2, VolumeX, Printer, MapPin, Phone,
  BellRing, Tag, UserCog, QrCode, Bell, Settings, BarChart3, Store, Coffee, Users, ClipboardList, BookOpen, Image, Copy, Trophy, Database, History, X, RefreshCw
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { format } from 'date-fns';
import QRCode from 'qrcode';

import OrdersManagement from '../components/admin/OrdersManagement';
import MenuManagement from '../components/admin/MenuManagement';
import CateringManagement from '../components/admin/CateringManagement';
import CampaignsManagement from '../components/admin/CampaignsManagement';
import QRMenuManagement from '../components/admin/QRMenuManagement';
import CouponsManagement from '../components/admin/CouponsManagement';
import UsersManagement from '../components/admin/UsersManagement';
import SettingsManagement from '../components/admin/SettingsManagement';
import SalesReport from '../components/admin/SalesReport';
import SalonSales from '../components/admin/SalonSales';
import BeverageManagement from '../components/admin/BeverageManagement';
import WaitersManagement from '../components/admin/WaitersManagement';
import VeresiyeManagement from '../components/admin/VeresiyeManagement';
import FlyerGenerator from '../components/admin/FlyerGenerator';
import BrochureGenerator from '../components/admin/BrochureGenerator';
import PushSubscribeButton from '../components/PushSubscribeButton';
import WaiterLeaderboard from '../components/admin/WaiterLeaderboard';
import AdminKpiStrip from '../components/admin/AdminKpiStrip';
import DataQuality from '../components/admin/DataQuality';

const WS_URL = process.env.REACT_APP_BACKEND_URL?.replace('https://', 'wss://').replace('http://', 'ws://');

// Helper: generate QR code as data URI
const generateQrDataUri = async (text) => {
  try {
    return await QRCode.toDataURL(text, { width: 120, margin: 1 });
  } catch { return ''; }
};

// Pure receipt generator functions - outside component for proper hoisting
const generateReceiptHtml = async (order) => {
  const itemsHtml = order.items?.map((item) => {
    const displayName = item.name_tr || item.name;
    let details = '';
    if (item.catering_selections) {
      const parts = [];
      const catLabels = {
        corbalar: 'Çorba', ana_yemekler: 'Ana Yemek', yardimci_yemekler: 'Yardımcı',
        salatalar: 'Salata', tatlilar: 'Tatlı', icecekler: 'İçecek', mezeler: 'Meze'
      };
      Object.entries(catLabels).forEach(([cat, label]) => {
        const selections = item.catering_selections[cat];
        if (selections?.length) {
          const itemsList = selections.map(s => `${s.quantity || 1}x ${s.code ? `[${s.code}] ` : ''}${s.name}`).join(', ');
          parts.push(`<div class="cd-row"><b>${label}:</b> ${itemsList}</div>`);
        }
      });
      if (parts.length) details = `<div class="cd">${parts.join('')}</div>`;
    }
    const qtyDisplay = item.quantity % 1 === 0 ? item.quantity : item.quantity.toFixed(1);
    return `<div class="itm"><div class="ir"><span class="qty">${qtyDisplay}x</span><span class="nm">${displayName}</span><span class="pr">€${(item.price * item.quantity).toFixed(2)}</span></div>${details}</div>`;
  }).join('') || '<div class="itm"><div class="ir"><span>Ürün bilgisi yok</span></div></div>';

  const orderTime = order.created_at ? format(new Date(order.created_at), 'dd.MM.yyyy HH:mm') : '-';
  const phoneNumber = order.user_phone || '';
  const phoneClean = phoneNumber.replace(/\s+/g, '').replace(/[^+0-9]/g, '');
  const phoneQrUrl = phoneClean ? await generateQrDataUri(`tel:${phoneClean}`) : '';
  const isTableOrder = order.table_number || order.order_type === 'table';
  const isSalonOrder = order.order_type === 'salon';
  const isPaketOrder = order.order_type === 'paket';
  const addr = order.address || {};
  const hasLocation = addr.latitude && addr.longitude;
  const mapQrUrl = hasLocation ? await generateQrDataUri(`https://maps.google.com/maps?q=${addr.latitude},${addr.longitude}`) : '';
  const orderTypeLabel = isSalonOrder ? 'SALON SATIŞI' : isPaketOrder ? 'PAKET SERVİS' : isTableOrder ? `MASA ${order.table_number || '-'}` : 'PAKET SERVİS';
  const addressSection = isSalonOrder ? '' : isTableOrder ? '' : `<div class="addr-box"><div class="addr-title">TESLİMAT ADRESİ</div><div class="addr-text">${addr.title || ''} - ${addr.address || ''}</div><div class="addr-text">${addr.district || ''}${addr.city ? ', ' + addr.city : ''}</div></div>`;
  const qrSection = (phoneQrUrl || mapQrUrl) ? `<div class="qr-row">${phoneQrUrl ? `<div class="qr-box"><div class="qr-label">TELEFON</div><img src="${phoneQrUrl}" alt="QR"/><div class="qr-num">${phoneNumber}</div></div>` : ''}${mapQrUrl ? `<div class="qr-box"><div class="qr-label">KONUM</div><img src="${mapQrUrl}" alt="MAP"/><div class="qr-num">Haritayı Aç</div></div>` : ''}</div>` : '';
  const itemsSubtotal = order.items?.reduce((s, i) => s + (i.price * i.quantity), 0) || 0;
  const hasDiscount = (order.discount_from_points > 0) || (order.discount_from_coupon > 0);
  const discountHtml = hasDiscount ? `<div class="disc-section"><div class="disc-subtotal"><span>ARA TOPLAM</span><span>€${itemsSubtotal.toFixed(2)}</span></div>${order.discount_from_points > 0 ? `<div class="disc-line"><span>PUAN İNDİRİMİ</span><span>-€${order.discount_from_points.toFixed(2)}</span></div>` : ''}${order.discount_from_coupon > 0 ? `<div class="disc-line"><span>KUPON${order.coupon_code ? ' (' + order.coupon_code + ')' : ''}</span><span>-€${order.discount_from_coupon.toFixed(2)}</span></div>` : ''}</div>` : '';

  return `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Adisyon #${order.id?.slice(-8).toUpperCase()}</title>
<style>*{margin:0;padding:0;box-sizing:border-box}body{font-family:'Courier New',monospace;width:80mm;max-width:302px;padding:6px 8px;font-size:14px;line-height:1.5;color:#000;background:#fff;-webkit-print-color-adjust:exact}.hdr{text-align:center;padding:8px 0 6px;border-bottom:3px double #000}.hdr h1{font-size:20px;font-weight:900;letter-spacing:2px;color:#000}.onum{text-align:center;font-size:32px;font-weight:900;padding:8px 0;margin:6px 0;border:3px solid #000;letter-spacing:3px;background:#f0f0f0}.otype{text-align:center;font-size:16px;font-weight:900;padding:5px 0;margin-bottom:5px;background:#000;color:#fff;letter-spacing:3px}.nfo{padding:6px 0;border-bottom:2px solid #000;font-size:13px}.nfo-row{display:flex;justify-content:space-between;margin:3px 0}.nfo-row b{font-weight:900}.sec-title{font-weight:900;font-size:15px;padding:6px 0 5px;border-bottom:2px solid #000;text-transform:uppercase;letter-spacing:1px}.itm{padding:6px 0;border-bottom:1px dashed #000}.itm:last-child{border-bottom:none}.ir{display:flex;align-items:baseline;gap:5px;font-size:15px}.ir .qty{font-weight:900;min-width:26px;font-size:17px}.ir .nm{flex:1;font-weight:800;font-size:15px}.ir .pr{font-weight:900;font-size:15px;text-align:right;white-space:nowrap}.cd{padding:5px 8px;margin:4px 0 0;border-left:4px solid #000;font-size:13px;font-weight:700;color:#000}.cd-row{margin:3px 0;line-height:1.4}.cd-row b{font-weight:900}.disc-section{padding:5px 0;border-top:1px dashed #000;margin-top:4px}.disc-subtotal{display:flex;justify-content:space-between;font-size:14px;font-weight:800;padding:3px 0;color:#000}.disc-line{display:flex;justify-content:space-between;font-size:14px;font-weight:900;padding:3px 0;color:#000;border:2px dashed #000;padding:4px 6px;margin:3px 0;background:#f0f0f0}.tot-box{margin:6px 0;border-top:3px double #000;border-bottom:3px double #000;padding:8px 0}.tot-row{display:flex;justify-content:space-between;align-items:center}.tot-row .tot-label{font-size:18px;font-weight:900;letter-spacing:2px}.tot-row .tot-val{font-size:26px;font-weight:900}.pay-badge{text-align:center;padding:6px;margin:5px 0;border:3px dashed #000;font-weight:900;font-size:16px;letter-spacing:3px}.addr-box{padding:6px;margin:5px 0;border:2px solid #000}.addr-title{font-weight:900;font-size:13px;margin-bottom:4px;text-transform:uppercase;letter-spacing:1px}.addr-text{font-size:13px;font-weight:700;line-height:1.4}.qr-row{display:flex;gap:10px;justify-content:center;margin:6px 0}.qr-box{text-align:center;padding:5px;border:2px solid #000}.qr-label{font-weight:900;font-size:11px;margin-bottom:3px;text-transform:uppercase;letter-spacing:1px}.qr-box img{width:90px;height:90px}.qr-num{font-weight:900;font-size:12px;margin-top:3px}.note-box{border:3px solid #000;padding:6px 8px;margin:5px 0;font-size:13px;font-weight:800}.note-box b{display:block;font-size:12px;text-transform:uppercase;margin-bottom:3px;font-weight:900}.ftr{text-align:center;padding:8px 0;margin-top:6px;border-top:2px dashed #000;font-size:12px;color:#000}.ftr .brand{font-size:16px;font-weight:900;display:block;margin-bottom:3px;letter-spacing:2px}.ftr .phone{font-weight:800;font-size:12px}@media print{body{width:80mm}@page{margin:0;size:80mm auto}}</style></head>
<body>
<div class="hdr"><h1>CENTRAL RESTAURANT</h1></div>
<div class="onum">#${order.id?.slice(-8).toUpperCase()}</div>
<div class="otype">${orderTypeLabel}</div>
<div class="nfo">
  <div class="nfo-row"><span>Tarih / Saat</span><b>${orderTime}</b></div>
  <div class="nfo-row"><span>Müşteri</span><b>${order.user_name || '-'}</b></div>
  ${phoneNumber ? `<div class="nfo-row"><span>Telefon</span><b>${phoneNumber}</b></div>` : ''}
  ${order.waiter_name ? `<div class="nfo-row"><span>Garson</span><b>${order.waiter_name}</b></div>` : ''}
  ${order.table_number ? `<div class="nfo-row"><span>Masa</span><b>${order.table_number}</b></div>` : ''}
</div>
<div class="sec-title">Sipariş (${order.items?.length || 0} Kalem)</div>
${itemsHtml}${discountHtml}
<div class="tot-box"><div class="tot-row"><span class="tot-label">TOPLAM</span><span class="tot-val">€${order.total_price?.toFixed(2) || '0.00'}</span></div></div>
<div class="pay-badge">${order.payment_method === 'nakit' ? 'KAPIDA NAKİT' : 'KAPIDA KART'}</div>
${addressSection}${qrSection}
${order.notes ? `<div class="note-box"><b>Müşteri Notu:</b>${order.notes}</div>` : ''}
<div class="ftr"><span class="brand">AFİYET OLSUN!</span><span class="phone">Central Hotel | +382 69 144 043</span></div>
</body></html>`;
};

const generateKitchenTicketHtml = (order) => {
  const orderTime = order.created_at ? format(new Date(order.created_at), 'dd.MM.yyyy HH:mm') : '-';
  const isTableOrder = order.table_number || order.order_type === 'table';
  const isSalonOrder = order.order_type === 'salon';
  const orderTypeLabel = isSalonOrder ? 'SALON' : isTableOrder ? `MASA ${order.table_number || '-'}` : 'PAKET';
  const itemsHtml = order.items?.map(item => {
    const displayName = item.name_tr || item.name;
    let details = '';
    if (item.catering_selections) {
      const catLabels = { corbalar: 'Çorba', ana_yemekler: 'Ana', yardimci_yemekler: 'Yan', salatalar: 'Salata', tatlilar: 'Tatlı', icecekler: 'İçecek', mezeler: 'Meze' };
      const parts = [];
      Object.entries(catLabels).forEach(([cat, label]) => {
        const selections = item.catering_selections[cat];
        if (selections?.length) parts.push(`<div class="kd">${label}: ${selections.map(s => `${s.quantity || 1}x ${s.code ? `[${s.code}] ` : ''}${s.name}`).join(', ')}</div>`);
      });
      if (parts.length) details = parts.join('');
    }
    const qtyDisplay = item.quantity % 1 === 0 ? item.quantity : item.quantity.toFixed(1);
    return `<div class="ki"><span class="kq">${qtyDisplay}x</span><span class="kn">${displayName}</span>${details}</div>`;
  }).join('') || '';

  return `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>MUTFAK #${order.id?.slice(-8).toUpperCase()}</title>
<style>*{margin:0;padding:0;box-sizing:border-box}body{font-family:'Courier New',monospace;width:80mm;max-width:302px;padding:8px;font-size:16px;line-height:1.4;color:#000;background:#fff;-webkit-print-color-adjust:exact}.kt{text-align:center;font-size:24px;font-weight:900;letter-spacing:3px;padding:6px 0;border-bottom:4px solid #000;margin-bottom:6px}.ko{text-align:center;font-size:42px;font-weight:900;padding:10px 0;letter-spacing:4px;border:4px solid #000;margin:6px 0;background:#f0f0f0}.ktype{text-align:center;font-size:22px;font-weight:900;padding:6px 0;background:#000;color:#fff;letter-spacing:4px;margin-bottom:6px}.ktime{text-align:center;font-size:14px;font-weight:700;margin-bottom:8px;color:#000}.ki{padding:8px 0;border-bottom:2px dashed #000;display:flex;flex-wrap:wrap;align-items:baseline;gap:6px}.ki:last-child{border-bottom:none}.kq{font-size:26px;font-weight:900;min-width:50px}.kn{font-size:20px;font-weight:800;flex:1}.kd{width:100%;font-size:14px;font-weight:700;padding:3px 0 0 50px;color:#000}.knote{border:4px solid #000;padding:8px;margin:8px 0;font-size:16px;font-weight:900}.knote b{display:block;font-size:14px;margin-bottom:4px;text-transform:uppercase}.kftr{text-align:center;padding:8px 0;margin-top:6px;border-top:3px double #000;font-size:12px;font-weight:700;letter-spacing:1px}@media print{body{width:80mm}@page{margin:0;size:80mm auto}}</style></head>
<body>
<div class="kt">MUTFAK</div>
<div class="ko">#${order.id?.slice(-8).toUpperCase()}</div>
<div class="ktype">${orderTypeLabel}</div>
<div class="ktime">${orderTime}${order.waiter_name ? ` | Garson: ${order.waiter_name}` : ''}</div>
${itemsHtml}
${order.notes ? `<div class="knote"><b>NOT:</b>${order.notes}</div>` : ''}
<div class="kftr">${order.items?.length || 0} KALEM</div>
</body></html>`;
};

const Admin = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('orders');
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [autoPrint, setAutoPrint] = useState(true);
  const [dualCopy, setDualCopy] = useState(() => localStorage.getItem('dualCopy') === 'true');
  const [kitchenTicket, setKitchenTicket] = useState(() => localStorage.getItem('kitchenTicket') !== 'false');
  const [newOrderAlert, setNewOrderAlert] = useState(null);
  const [printedOrders, setPrintedOrders] = useState(new Set());
  const [printCounts, setPrintCounts] = useState(() => {
    try { return JSON.parse(localStorage.getItem('printCounts') || '{}'); } catch { return {}; }
  });
  const [audioReady, setAudioReady] = useState(() => localStorage.getItem('audioPrefSet') === 'true');
  const [notifPermission, setNotifPermission] = useState('default');
  const [printQueueLen, setPrintQueueLen] = useState(0);
  const [printHistory, setPrintHistory] = useState(() => {
    try { return JSON.parse(localStorage.getItem('printHistory') || '[]'); } catch { return []; }
  });
  const [showHistory, setShowHistory] = useState(false);
  const audioCtxRef = useRef(null);
  const audioBufferRef = useRef(null);
  const wsRef = useRef(null);
  const soundEnabledRef = useRef(true);
  const autoPrintRef = useRef(true);
  const dualCopyRef = useRef(dualCopy);
  const kitchenTicketRef = useRef(kitchenTicket);
  const handleNewOrderRef = useRef(null);
  const printQueueRef = useRef([]);
  const isPrintingRef = useRef(false);
  const printOrderRef = useRef(null);

  useEffect(() => { soundEnabledRef.current = soundEnabled; }, [soundEnabled]);
  useEffect(() => { autoPrintRef.current = autoPrint; }, [autoPrint]);
  useEffect(() => { dualCopyRef.current = dualCopy; localStorage.setItem('dualCopy', dualCopy); }, [dualCopy]);
  useEffect(() => { kitchenTicketRef.current = kitchenTicket; localStorage.setItem('kitchenTicket', kitchenTicket); }, [kitchenTicket]);
  useEffect(() => { localStorage.setItem('printCounts', JSON.stringify(printCounts)); }, [printCounts]);

  // Request browser notification permission
  useEffect(() => {
    if ('Notification' in window) {
      setNotifPermission(Notification.permission);
      if (Notification.permission === 'default') {
        Notification.requestPermission().then(p => setNotifPermission(p));
      }
    }
  }, []);

  // Generate a pleasant notification chime using Web Audio API oscillators
  // No external file needed - works everywhere AudioContext is supported
  const generateNotificationBuffer = useCallback((ctx) => {
    const sampleRate = ctx.sampleRate;
    const duration = 1.2;
    const buffer = ctx.createBuffer(1, sampleRate * duration, sampleRate);
    const data = buffer.getChannelData(0);
    // Three-tone ascending chime: E5 → G5 → C6 (pleasant restaurant bell)
    const tones = [
      { freq: 659.25, start: 0, end: 0.35 },    // E5
      { freq: 783.99, start: 0.2, end: 0.55 },   // G5
      { freq: 1046.50, start: 0.4, end: 1.0 },   // C6 (longer, fades out)
    ];
    for (let i = 0; i < buffer.length; i++) {
      const t = i / sampleRate;
      let sample = 0;
      for (const tone of tones) {
        if (t >= tone.start && t <= tone.end) {
          const localT = t - tone.start;
          const dur = tone.end - tone.start;
          // Attack-decay envelope
          const attack = Math.min(1, localT / 0.01);
          const decay = Math.exp(-3 * localT / dur);
          const envelope = attack * decay;
          // Sine wave with slight harmonic richness
          sample += envelope * 0.4 * (
            Math.sin(2 * Math.PI * tone.freq * localT) * 0.8 +
            Math.sin(2 * Math.PI * tone.freq * 2 * localT) * 0.15 +
            Math.sin(2 * Math.PI * tone.freq * 3 * localT) * 0.05
          );
        }
      }
      data[i] = Math.max(-1, Math.min(1, sample));
    }
    return buffer;
  }, []);

  // Initialize Web Audio API with synthesized sound (no file dependency)
  const initAudio = useCallback(async () => {
    try {
      // Create or resume AudioContext
      if (!audioCtxRef.current) {
        const AC = window.AudioContext || window.webkitAudioContext;
        audioCtxRef.current = new AC();
      }
      if (audioCtxRef.current.state === 'suspended') {
        await audioCtxRef.current.resume();
      }
      // Generate notification sound buffer (no fetch needed)
      if (!audioBufferRef.current) {
        audioBufferRef.current = generateNotificationBuffer(audioCtxRef.current);
      }
      // Also try to load bell.mp3 as a richer alternative (non-blocking)
      fetch('/bell.mp3').then(r => r.arrayBuffer()).then(ab => {
        audioCtxRef.current?.decodeAudioData(ab).then(decoded => {
          audioBufferRef.current = decoded; // Replace synth with real bell
        }).catch(() => {});
      }).catch(() => {});
      setAudioReady(true);
      setSoundEnabled(true);
      return true;
    } catch (e) {
      console.error('Audio init error:', e);
      return false;
    }
  }, [generateNotificationBuffer]);

  const playNotificationSound = useCallback(async () => {
    if (!soundEnabledRef.current) return;
    // Trigger device vibration as a robust backup (Android & most mobiles).
    // iOS Safari ignores this, but Web Audio below still works after a gesture.
    try {
      if (navigator.vibrate) navigator.vibrate([220, 100, 220, 100, 220]);
    } catch {}
    // Ensure AudioContext exists (mobile users may have audioPrefSet=true from a previous session)
    if (!audioCtxRef.current) {
      try {
        const AC = window.AudioContext || window.webkitAudioContext;
        if (AC) {
          audioCtxRef.current = new AC();
          if (!audioBufferRef.current) {
            audioBufferRef.current = generateNotificationBuffer(audioCtxRef.current);
          }
        }
      } catch {}
    }
    // Play using Web Audio API
    if (audioCtxRef.current && audioBufferRef.current) {
      try {
        if (audioCtxRef.current.state === 'suspended') {
          await audioCtxRef.current.resume();
        }
        const source = audioCtxRef.current.createBufferSource();
        source.buffer = audioBufferRef.current;
        const gainNode = audioCtxRef.current.createGain();
        gainNode.gain.value = 1.0;
        source.connect(gainNode);
        gainNode.connect(audioCtxRef.current.destination);
        source.start(0);
        return;
      } catch (e) {
        console.error('Web Audio play error:', e);
      }
    }
    // Last resort fallback: try HTML5 Audio
    try {
      const a = new Audio('/bell.mp3');
      a.volume = 1.0;
      await a.play();
    } catch (e) {}
  }, [generateNotificationBuffer]);

  // Show browser notification for background tabs
  const showBrowserNotification = useCallback((order) => {
    if (!('Notification' in window) || Notification.permission !== 'granted') return;
    const payload = {
      title: 'Yeni Sipariş!',
      body: `#${order.id?.slice(-8).toUpperCase()} - ${order.user_name || 'Misafir'} - €${order.total_price?.toFixed(2)}`,
      tag: 'new-order-' + order.id,
      url: '/admin'
    };
    // Prefer Service Worker notification (works when tab is in background on mobile)
    if (navigator.serviceWorker && navigator.serviceWorker.controller) {
      try {
        navigator.serviceWorker.controller.postMessage({ type: 'NOTIFY', payload });
        return;
      } catch {}
    }
    try {
      const n = new Notification(payload.title, {
        body: payload.body,
        icon: '/logo.png',
        tag: payload.tag,
        requireInteraction: true
      });
      n.onclick = () => { window.focus(); n.close(); };
    } catch (e) {}
  }, []);

  // Mobile-friendly audio priming:
  // If user previously dismissed the audio modal (audioPrefSet=true), AudioContext
  // is not yet created. The FIRST tap/click anywhere on the page will silently
  // initialize Web Audio so subsequent new-order events can ring on mobile.
  useEffect(() => {
    if (!audioReady) return;
    if (audioCtxRef.current) return; // already initialized
    const prime = () => {
      initAudio().catch(() => {});
      window.removeEventListener('touchstart', prime);
      window.removeEventListener('click', prime);
      window.removeEventListener('keydown', prime);
    };
    window.addEventListener('touchstart', prime, { once: true, passive: true });
    window.addEventListener('click', prime, { once: true });
    window.addEventListener('keydown', prime, { once: true });
    return () => {
      window.removeEventListener('touchstart', prime);
      window.removeEventListener('click', prime);
      window.removeEventListener('keydown', prime);
    };
  }, [audioReady, initAudio]);

  const handleNewOrder = useCallback((order) => {
    setNewOrderAlert(order);
    playNotificationSound();
    showBrowserNotification(order);
    if (autoPrintRef.current && printOrderRef.current) {
      setTimeout(() => { printOrderRef.current(order); }, 1000);
    }
    toast.success(`Yeni Sipariş! #${order.id.slice(-8).toUpperCase()}`, {
      description: `${order.user_name || 'Misafir'} - €${order.total_price?.toFixed(2) || '0.00'}`,
      duration: 10000
    });
    setTimeout(() => setNewOrderAlert(null), 30000);
  }, [playNotificationSound, showBrowserNotification]);
  handleNewOrderRef.current = handleNewOrder;

  useEffect(() => {
    if (!WS_URL) return;
    let reconnectTimeout;
    const connectWebSocket = () => {
      const ws = new WebSocket(`${WS_URL}/api/ws/orders`);
      wsRef.current = ws;
      ws.onopen = () => console.log('WebSocket connected');
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'new_order' && handleNewOrderRef.current) {
            handleNewOrderRef.current(data.order);
          }
        } catch (e) {}
      };
      ws.onclose = () => { reconnectTimeout = setTimeout(connectWebSocket, 3000); };
      ws.onerror = () => {};
    };
    connectWebSocket();
    return () => { clearTimeout(reconnectTimeout); wsRef.current?.close(); };
  }, []);

  const testSound = async () => {
    const ok = await initAudio();
    if (ok) {
      await playNotificationSound();
      toast.success('Ses aktif! Yeni sipariş geldiğinde bildirim duyacaksınız.', { duration: 3000 });
    } else {
      toast.error('Ses başlatılamadı. Lütfen sayfayı yenileyip tekrar deneyin.', { duration: 5000 });
    }
    // Persist preference so the modal does not show again
    localStorage.setItem('audioPrefSet', 'true');
    // Also request notification permission
    if ('Notification' in window && Notification.permission === 'default') {
      const p = await Notification.requestPermission();
      setNotifPermission(p);
    }
  };

  const markOrderPrinted = (orderId) => {
    setPrintedOrders(prev => new Set([...prev, orderId]));
    setPrintCounts(prev => ({ ...prev, [orderId]: (prev[orderId] || 0) + 1 }));
  };

  // --- Print Queue System (isolated iframe; preserves classic 80mm thermal receipt look) ---
  const executePrint = useCallback((html) => {
    return new Promise((resolve) => {
      let resolved = false;
      const safeResolve = () => {
        if (resolved) return;
        resolved = true;
        try { if (iframe.parentNode) iframe.parentNode.removeChild(iframe); } catch (e) {}
        resolve();
      };

      const iframe = document.createElement('iframe');
      iframe.setAttribute('aria-hidden', 'true');
      iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden;';
      document.body.appendChild(iframe);

      const doc = iframe.contentDocument || iframe.contentWindow.document;
      doc.open();
      doc.write(html);
      doc.close();

      const triggerPrint = () => {
        try {
          iframe.contentWindow.addEventListener('afterprint', () => setTimeout(safeResolve, 400));
        } catch (e) {}
        try {
          iframe.contentWindow.focus();
          iframe.contentWindow.print();
        } catch (e) {
          console.error('Print error:', e);
          safeResolve();
          return;
        }
        // Safety fallback if afterprint never fires (e.g. silent dialog dismissal)
        setTimeout(safeResolve, 8000);
      };

      if (iframe.contentWindow && iframe.contentWindow.document.readyState === 'complete') {
        setTimeout(triggerPrint, 250);
      } else {
        iframe.onload = () => setTimeout(triggerPrint, 250);
      }
    });
  }, []);

  const processQueue = useCallback(async () => {
    if (isPrintingRef.current || printQueueRef.current.length === 0) return;
    isPrintingRef.current = true;
    const job = printQueueRef.current.shift();
    setPrintQueueLen(printQueueRef.current.length);
    
    try {
      const html = job.type === 'kitchen'
        ? generateKitchenTicketHtml(job.order)
        : await generateReceiptHtml(job.order);
      await executePrint(html);
      markOrderPrinted(job.order.id);
      // Append to print history (newest first, max 20)
      setPrintHistory(prev => {
        const entry = {
          orderId: job.order.id,
          type: job.type,
          table: job.order.table_number,
          channel: job.order.channel,
          total: job.order.total_price,
          printedAt: new Date().toISOString()
        };
        const next = [entry, ...prev].slice(0, 20);
        try { localStorage.setItem('printHistory', JSON.stringify(next)); } catch {}
        return next;
      });
    } catch (e) {
      console.error('Print job failed:', e);
    }
    
    isPrintingRef.current = false;
    if (printQueueRef.current.length > 0) {
      setTimeout(() => processQueue(), 1500);
    }
  }, [executePrint]);

  const queuePrint = useCallback((order, type = 'full') => {
    printQueueRef.current.push({ order, type });
    setPrintQueueLen(printQueueRef.current.length);
    processQueue();
  }, [processQueue]);

  const printOrder = useCallback((order, type) => {
    if (type === 'kitchen') {
      queuePrint(order, 'kitchen');
    } else if (type === 'full') {
      queuePrint(order, 'full');
    } else {
      // Default: respect dual copy & kitchen ticket settings
      if (kitchenTicketRef.current) {
        queuePrint(order, 'kitchen');
      }
      if (dualCopyRef.current || !kitchenTicketRef.current) {
        queuePrint(order, 'full');
      }
    }
  }, [queuePrint]);
  printOrderRef.current = printOrder;
  
  return (
    <div className="min-h-screen bg-bone pb-8">
      {!audioReady && (
        <div className="fixed inset-0 bg-charcoal/95 z-[100] flex items-center justify-center p-4">
          <div className="text-center max-w-sm">
            <div className="w-24 h-24 bg-terracotta rounded-full flex items-center justify-center mx-auto mb-6 animate-pulse">
              <Volume2 className="w-12 h-12 text-white" />
            </div>
            <h2 className="font-playfair text-3xl font-bold text-white mb-3">Ses Bildirimini Aç</h2>
            <p className="text-stone-400 mb-4">Yeni sipariş geldiğinde sesli bildirim almak için aşağıdaki butona tıklayın</p>
            <p className="text-stone-500 text-xs mb-6">Tarayıcı bildirimleri de etkinleştirilecektir</p>
            <button data-testid="enable-sound-btn" onClick={testSound}
              className="w-full py-4 px-8 bg-terracotta hover:bg-terracotta/90 text-white text-xl font-bold rounded-2xl transition-all transform hover:scale-105 active:scale-95 shadow-lg">
              <BellRing className="w-6 h-6 inline mr-3" />SESİ AÇ
            </button>
            <button onClick={() => { setAudioReady(true); initAudio(); localStorage.setItem('audioPrefSet', 'true'); }} className="mt-4 text-stone-500 hover:text-stone-300 text-sm underline" data-testid="audio-skip-btn">Sessiz devam et (bir daha sorma)</button>
          </div>
        </div>
      )}

      <AnimatePresence>
        {newOrderAlert && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setNewOrderAlert(null)}>
            <motion.div initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, y: 20 }}
              className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-center mb-4">
                <div className="w-16 h-16 bg-terracotta rounded-full flex items-center justify-center animate-pulse">
                  <BellRing className="w-8 h-8 text-white" />
                </div>
              </div>
              <h2 className="font-playfair text-2xl font-bold text-center text-charcoal mb-2">Yeni Sipariş!</h2>
              <p className="text-center text-charcoal/60 mb-4">#{newOrderAlert.id?.slice(-8).toUpperCase()}</p>
              <div className="bg-sand rounded-xl p-4 space-y-2 mb-4">
                <div className="flex items-center gap-2"><Phone className="w-4 h-4 text-charcoal/60" /><span className="font-medium">{newOrderAlert.user_name}</span><span className="text-charcoal/60">- {newOrderAlert.user_phone}</span></div>
                <div className="flex items-center gap-2"><MapPin className="w-4 h-4 text-charcoal/60" /><span className="text-sm text-charcoal/70">{newOrderAlert.address?.district}, {newOrderAlert.address?.city}</span></div>
                <div className="flex items-center justify-between pt-2 border-t border-stone-300"><span className="text-charcoal/60">{newOrderAlert.items_count} ürün</span><span className="font-playfair text-xl font-bold text-terracotta">€{newOrderAlert.total_price?.toFixed(2)}</span></div>
              </div>
              <div className="flex gap-2">
                <Button onClick={() => printOrder(newOrderAlert, 'kitchen')} className="flex-1 bg-amber-600 text-white hover:bg-amber-500" data-testid="alert-print-kitchen"><ChefHat className="w-4 h-4 mr-1" />Mutfak</Button>
                <Button onClick={() => printOrder(newOrderAlert, 'full')} className="flex-1 bg-basil text-white hover:bg-basil/90" data-testid="alert-print-full"><Printer className="w-4 h-4 mr-1" />Adisyon</Button>
                <Button onClick={() => setNewOrderAlert(null)} variant="outline" className="flex-1">Kapat</Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Print History Drawer */}
      <AnimatePresence>
        {showHistory && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[90] bg-black/40 backdrop-blur-sm"
            onClick={() => setShowHistory(false)}
          >
            <motion.div
              initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 28, stiffness: 250 }}
              className="absolute right-0 top-0 bottom-0 w-full max-w-md bg-white shadow-2xl overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
              data-testid="print-history-drawer"
            >
              <div className="sticky top-0 bg-charcoal text-white px-5 py-4 flex items-center justify-between z-10">
                <div className="flex items-center gap-2">
                  <History className="w-5 h-5 text-terracotta" />
                  <h3 className="font-playfair text-lg font-bold">Yazdırma Geçmişi</h3>
                  <span className="text-xs text-white/60">(Son {printHistory.length})</span>
                </div>
                <div className="flex items-center gap-2">
                  {printHistory.length > 0 && (
                    <button
                      onClick={() => { setPrintHistory([]); localStorage.removeItem('printHistory'); toast.success('Geçmiş temizlendi'); }}
                      className="text-xs text-white/60 hover:text-white underline"
                      data-testid="clear-print-history-btn"
                    >Temizle</button>
                  )}
                  <button onClick={() => setShowHistory(false)} className="p-1 hover:bg-white/10 rounded-full" data-testid="close-print-history-btn">
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>
              {printHistory.length === 0 ? (
                <div className="text-center py-16 text-charcoal/50">
                  <Printer className="w-12 h-12 mx-auto mb-3 opacity-40" />
                  <p>Henüz yazdırma kaydı yok</p>
                </div>
              ) : (
                <div className="p-4 space-y-2">
                  {printHistory.map((h, idx) => {
                    const dt = new Date(h.printedAt);
                    const timeStr = dt.toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
                    const typeLabel = h.type === 'kitchen' ? 'Mutfak' : 'Adisyon';
                    const typeColor = h.type === 'kitchen' ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700';
                    return (
                      <div key={idx} className="border border-stone-200 rounded-xl p-3 hover:bg-stone-50 transition-colors">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${typeColor}`}>{typeLabel}</span>
                              {h.table && <span className="text-xs font-medium text-charcoal">Masa {h.table}</span>}
                              {!h.table && h.channel && <span className="text-[10px] text-charcoal/60 uppercase">{h.channel}</span>}
                            </div>
                            <p className="font-mono text-xs text-charcoal/60 mt-1 truncate">#{h.orderId?.slice(0, 8)}</p>
                            <p className="text-[11px] text-charcoal/50 mt-0.5">{timeStr}{h.total ? ` • €${Number(h.total).toFixed(2)}` : ''}</p>
                          </div>
                          <button
                            onClick={async () => {
                              try {
                                const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/orders/${h.orderId}`, {
                                  headers: { Authorization: `Bearer ${token}` }
                                });
                                if (!res.ok) throw new Error('not found');
                                const order = await res.json();
                                if (printOrderRef.current) printOrderRef.current(order, h.type);
                                toast.success('Yeniden yazdırma kuyruğa eklendi');
                              } catch {
                                toast.error('Sipariş bulunamadı, yeniden yazdırılamadı');
                              }
                            }}
                            className="flex-shrink-0 flex items-center gap-1 px-2.5 py-1.5 bg-basil text-white text-xs font-medium rounded-full hover:bg-basil/90 active:scale-95 transition-all"
                            data-testid={`reprint-${idx}`}
                          >
                            <RefreshCw className="w-3 h-3" /> Yeniden Yazdır
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="bg-charcoal py-4 sm:py-6">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-3 flex-shrink-0">
              <LayoutDashboard className="w-6 h-6 sm:w-7 sm:h-7 text-terracotta" />
              <h1 className="font-playfair text-xl sm:text-2xl font-bold text-white">Admin Panel</h1>
            </div>
            <div className="flex items-center gap-1.5 flex-wrap justify-end">
              <div className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-xs ${audioReady ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                <div className={`w-2 h-2 rounded-full ${audioReady ? 'bg-green-400' : 'bg-yellow-400 animate-pulse'}`} />
                <span className="hidden md:inline">{audioReady ? 'Ses Hazır' : 'Tıklayın'}</span>
              </div>
              <button
                onClick={testSound}
                className="flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-terracotta/20 text-terracotta hover:bg-terracotta/30 transition-colors"
                data-testid="admin-test-sound-btn"
                title="Sipariş bildirim sesini test et"
              >
                🔔 <span className="hidden md:inline">Test</span>
              </button>
              <PushSubscribeButton token={token} />
              <button onClick={() => setSoundEnabled(!soundEnabled)} className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full transition-colors ${soundEnabled ? 'bg-basil text-white' : 'bg-stone-600 text-stone-300'}`} title={soundEnabled ? 'Sesi kapat' : 'Sesi aç'}>
                {soundEnabled ? <Volume2 className="w-3.5 h-3.5" /> : <VolumeX className="w-3.5 h-3.5" />}<span className="text-xs font-medium hidden md:inline">Ses</span>
              </button>
              <button onClick={() => setAutoPrint(!autoPrint)} className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full transition-colors ${autoPrint ? 'bg-terracotta text-white' : 'bg-stone-600 text-stone-300'}`} data-testid="auto-print-toggle" title={autoPrint ? 'Otomatik yazdırma' : 'Manuel'}>
                <Printer className="w-3.5 h-3.5" /><span className="text-xs font-medium hidden md:inline">{autoPrint ? 'Oto' : 'Manuel'}</span>
              </button>
              <button onClick={() => setKitchenTicket(!kitchenTicket)} className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full transition-colors ${kitchenTicket ? 'bg-amber-600 text-white' : 'bg-stone-600 text-stone-300'}`} data-testid="kitchen-ticket-toggle" title="Mutfak fişi">
                <ChefHat className="w-3.5 h-3.5" /><span className="text-xs font-medium hidden md:inline">Mutfak</span>
              </button>
              <button onClick={() => setDualCopy(!dualCopy)} className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full transition-colors ${dualCopy ? 'bg-blue-600 text-white' : 'bg-stone-600 text-stone-300'}`} data-testid="dual-copy-toggle" title="Çift kopya (Mutfak + Adisyon)">
                <Copy className="w-3.5 h-3.5" /><span className="text-xs font-medium hidden md:inline">{dualCopy ? '2 Kopya' : '1 Kopya'}</span>
              </button>
              {printQueueLen > 0 && (
                <div className="flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-terracotta/20 text-terracotta animate-pulse">
                  <Printer className="w-3 h-3" />
                  <span>{printQueueLen}</span>
                </div>
              )}
              <button onClick={() => setShowHistory(true)} className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-stone-700 text-stone-200 hover:bg-stone-600 transition-colors" data-testid="print-history-btn" title="Yazdırma Geçmişi">
                <History className="w-3.5 h-3.5" /><span className="text-xs font-medium hidden md:inline">Geçmiş</span>
                {printHistory.length > 0 && (
                  <span className="bg-terracotta text-white text-[10px] px-1.5 rounded-full font-bold">{printHistory.length}</span>
                )}
              </button>
              <button onClick={() => navigate('/waiter')} className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-basil text-white hover:bg-basil/90 transition-colors" data-testid="admin-waiter-screen-btn" title="Garson Ekranı">
                <ClipboardList className="w-3.5 h-3.5" /><span className="text-xs font-medium hidden md:inline">Garson</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <AdminKpiStrip token={token} />
          <TabsList className="flex flex-wrap gap-1 w-full max-w-7xl mb-6 h-auto p-1">
            <TabsTrigger value="orders" className="gap-1 text-xs md:text-sm"><Package className="w-4 h-4" /><span className="hidden sm:inline">Siparisler</span></TabsTrigger>
            <TabsTrigger value="salon" className="gap-1 text-xs md:text-sm"><Store className="w-4 h-4" /><span className="hidden sm:inline">Salon</span></TabsTrigger>
            <TabsTrigger value="veresiye" className="gap-1 text-xs md:text-sm"><BookOpen className="w-4 h-4" /><span className="hidden sm:inline">Veresiye</span></TabsTrigger>
            <TabsTrigger value="waiters" className="gap-1 text-xs md:text-sm"><UserCog className="w-4 h-4" /><span className="hidden sm:inline">Garsonlar</span></TabsTrigger>
            <TabsTrigger value="flyer" className="gap-1 text-xs md:text-sm"><Image className="w-4 h-4" /><span className="hidden sm:inline">Flyer</span></TabsTrigger>
            <TabsTrigger value="brochure" className="gap-1 text-xs md:text-sm"><ClipboardList className="w-4 h-4" /><span className="hidden sm:inline">Brosur</span></TabsTrigger>
            <TabsTrigger value="menu" className="gap-1 text-xs md:text-sm"><UtensilsCrossed className="w-4 h-4" /><span className="hidden sm:inline">Menu</span></TabsTrigger>
            <TabsTrigger value="beverages" className="gap-1 text-xs md:text-sm"><Coffee className="w-4 h-4" /><span className="hidden sm:inline">Icecekler</span></TabsTrigger>
            <TabsTrigger value="catering" className="gap-1 text-xs md:text-sm"><ChefHat className="w-4 h-4" /><span className="hidden sm:inline">Catering</span></TabsTrigger>
            <TabsTrigger value="qrmenu" className="gap-1 text-xs md:text-sm"><QrCode className="w-4 h-4" /><span className="hidden sm:inline">QR Menu</span></TabsTrigger>
            <TabsTrigger value="reports" className="gap-1 text-xs md:text-sm"><BarChart3 className="w-4 h-4" /><span className="hidden sm:inline">Raporlar</span></TabsTrigger>
            <TabsTrigger value="leaderboard" className="gap-1 text-xs md:text-sm"><Trophy className="w-4 h-4" /><span className="hidden sm:inline">Liderlik</span></TabsTrigger>
            <TabsTrigger value="data_quality" className="gap-1 text-xs md:text-sm"><Database className="w-4 h-4" /><span className="hidden sm:inline">Veri Kalitesi</span></TabsTrigger>
            <TabsTrigger value="users" className="gap-1 text-xs md:text-sm"><Users className="w-4 h-4" /><span className="hidden sm:inline">Uyeler</span></TabsTrigger>
            <TabsTrigger value="coupons" className="gap-1 text-xs md:text-sm"><Tag className="w-4 h-4" /><span className="hidden sm:inline">Kuponlar</span></TabsTrigger>
            <TabsTrigger value="campaigns" className="gap-1 text-xs md:text-sm"><Sparkles className="w-4 h-4" /><span className="hidden sm:inline">Kampanyalar</span></TabsTrigger>
            <TabsTrigger value="settings" className="gap-1 text-xs md:text-sm"><Settings className="w-4 h-4" /><span className="hidden sm:inline">Ayarlar</span></TabsTrigger>
          </TabsList>
          <TabsContent value="orders"><OrdersManagement token={token} onPrint={printOrder} printedOrders={printedOrders} onMarkPrinted={markOrderPrinted} printCounts={printCounts} /></TabsContent>
          <TabsContent value="salon"><SalonSales token={token} onPrint={printOrder} /></TabsContent>
          <TabsContent value="veresiye"><VeresiyeManagement token={token} /></TabsContent>
          <TabsContent value="waiters"><WaitersManagement token={token} /></TabsContent>
          <TabsContent value="flyer"><FlyerGenerator token={token} /></TabsContent>
          <TabsContent value="brochure"><BrochureGenerator /></TabsContent>
          <TabsContent value="menu"><MenuManagement token={token} /></TabsContent>
          <TabsContent value="beverages"><BeverageManagement token={token} /></TabsContent>
          <TabsContent value="catering"><CateringManagement token={token} onMenuSaved={() => setActiveTab('flyer')} /></TabsContent>
          <TabsContent value="qrmenu"><QRMenuManagement token={token} /></TabsContent>
          <TabsContent value="users"><UsersManagement token={token} /></TabsContent>
          <TabsContent value="coupons"><CouponsManagement token={token} /></TabsContent>
          <TabsContent value="campaigns"><CampaignsManagement token={token} /></TabsContent>
          <TabsContent value="reports"><SalesReport token={token} /></TabsContent>
          <TabsContent value="leaderboard"><WaiterLeaderboard token={token} /></TabsContent>
          <TabsContent value="data_quality"><DataQuality token={token} /></TabsContent>
          <TabsContent value="settings"><SettingsManagement token={token} /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Admin;
